import cn from 'classnames';
import { type FC, type ReactNode, useRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import sanitizeHtml from 'sanitize-html';

import {
    trackPromoClick,
    trackPromoImpressions,
    buildPromoTrackingObject,
} from '../helpers/promoTrackingHelpers';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet';

// components
import { Link } from 'dibs-elements/exports/Link';
import { Button } from 'dibs-elements/exports/Button';
import { VideoTile } from 'dibs-elements/exports/VideoTile';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';

// styles
import dibsCss from 'dibs-css';
import styles from './HpSharedTopHeroBannerModule.module.css';

import { type HpSharedTopHeroBannerModule_componentModule$key } from './__generated__/HpSharedTopHeroBannerModule_componentModule.graphql';

export const HpSharedTopHeroBannerModule: FC<{
    componentModule: HpSharedTopHeroBannerModule_componentModule$key;
    isMobile?: boolean;
}> = ({ componentModule: componentModuleRef, isMobile }) => {
    const componentModule = useFragment(
        graphql`
            fragment HpSharedTopHeroBannerModule_componentModule on TopHeroBannerModule {
                messages(userType: $userType) {
                    items {
                        ... on TopHeroBannerItem @skip(if: $isMobile) {
                            imageUrl
                            imageHeight
                            imageWidth
                        }
                        ... on TopHeroBannerItem @include(if: $isMobile) {
                            mobileImageUrl
                            mobileImageHeight
                            mobileImageWidth
                        }
                        heroBannerSettings {
                            eyeBrowText
                            headerText
                            dekText
                            backgroundColor
                        }
                        cta
                        linkData {
                            path
                        }
                        videoUrl
                        videoAutoplay
                        mediaFormat
                    }
                }
            }
        `,
        componentModuleRef
    );

    const heroBannerRef = useRef(null);

    // We do show only single banner item
    const bannerItem = componentModule.messages?.items?.[0];

    if (!bannerItem) {
        return null;
    }

    const {
        imageUrl: desktopImageUrl,
        mobileImageUrl,
        imageWidth,
        imageHeight,
        mobileImageWidth,
        mobileImageHeight,
        heroBannerSettings,
        cta,
        linkData,
        videoUrl,
        videoAutoplay,
        mediaFormat,
    } = bannerItem;
    const { eyeBrowText, headerText, dekText, backgroundColor } = heroBannerSettings || {};

    const imageUrl = desktopImageUrl || mobileImageUrl || '';
    const topic = eyeBrowText || '';
    const title = sanitizeHtml(headerText || '', { allowedTags: [] });
    const description = sanitizeHtml(dekText || '', { allowedTags: [] });

    const promoTrackingObject = buildPromoTrackingObject({
        index: 0,
        id: 'banner-carousel',
        name: 'Banner Carousel',
        item: { linkData },
    });

    const getMediaComponent = (): JSX.Element | null => {
        if (mediaFormat === 'upload-video' && videoUrl) {
            const mediaHeight = imageHeight || mobileImageHeight;
            const mediaWidth = imageWidth || mobileImageWidth;
            const aspectRatio = (Number(mediaHeight) / Number(mediaWidth)) * 100;

            return (
                <div
                    className={cn(dibsCss.relative, dibsCss.wFull)}
                    style={{ paddingBottom: `${aspectRatio}%` }}
                >
                    <VideoTile
                        sourceUrl={videoUrl}
                        noSpinner
                        loop={false}
                        autoPlay={!!videoAutoplay}
                    />
                </div>
            );
        }

        if (imageUrl) {
            return (
                <img
                    alt={title}
                    src={imageUrl}
                    sizes={isMobile ? '100vw' : '50vw'}
                    srcSet={getSrcsetString(
                        imageUrl,
                        isMobile ? undefined : [960, 1280, 1600, 1920]
                    )}
                    className={cn(styles.image, { [dibsCss.wFull]: isMobile })}
                />
            );
        }

        return null;
    };

    const getBannerWrapper = ({ children }: { children: ReactNode }): JSX.Element => {
        const path = linkData?.path;

        if (path) {
            return (
                <Link
                    href={path}
                    className={styles.link}
                    onClick={() => trackPromoClick(promoTrackingObject)}
                >
                    {children}
                </Link>
            );
        }

        return <>{children}</>;
    };

    return (
        <>
            <VisibilityTracker
                elementRef={heroBannerRef}
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        trackPromoImpressions([promoTrackingObject]);
                    }
                }}
            />
            {getBannerWrapper({
                children: (
                    <div
                        ref={heroBannerRef}
                        className={cn(styles.container, dibsCss.wScreen, dibsCss.flex, {
                            [dibsCss.flexCol]: isMobile,
                            [styles.isMobile]: isMobile,
                        })}
                        style={{ backgroundColor: backgroundColor || undefined }}
                    >
                        {isMobile ? (
                            <>
                                {getMediaComponent()}
                                <div
                                    className={cn(
                                        dibsCss.flex,
                                        dibsCss.flexCol,
                                        dibsCss.pxSmall,
                                        dibsCss.pyMedium
                                    )}
                                >
                                    <span
                                        className={cn(
                                            dibsCss.sassyFontHeaderSmallItalic,
                                            dibsCss.mbXxsmall
                                        )}
                                    >
                                        {topic}
                                    </span>
                                    <span
                                        className={cn(
                                            dibsCss.sassyFontHeaderXxLarge,
                                            dibsCss.mbSmall
                                        )}
                                    >
                                        {title}
                                    </span>
                                    <div>
                                        <Button
                                            className={styles.button}
                                            size="large"
                                            fullWidth={false}
                                            type="transparent"
                                        >
                                            {cta}
                                        </Button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className={dibsCss.w50}>{getMediaComponent()}</div>
                                <div
                                    className={cn(
                                        dibsCss.w50,
                                        dibsCss.flex,
                                        dibsCss.flexCol,
                                        styles.textArea,
                                        dibsCss.justifyCenter
                                    )}
                                >
                                    <span
                                        className={cn(
                                            dibsCss.mbXsmall,
                                            dibsCss.sassyFontHeaderMediumItalic
                                        )}
                                    >
                                        {topic}
                                    </span>
                                    <span
                                        className={cn(
                                            styles.title,
                                            dibsCss.mbSmall,
                                            dibsCss.sassyFontHeaderSizeXLargeTypeNormal
                                        )}
                                    >
                                        {title}
                                    </span>
                                    <span
                                        className={cn(
                                            dibsCss.mbMedium,
                                            styles.description,
                                            dibsCss.sassyFontHeaderLargeNormal
                                        )}
                                    >
                                        {description}
                                    </span>
                                    {cta && (
                                        <div>
                                            <Button
                                                className={styles.button}
                                                size="large"
                                                fullWidth={false}
                                                type="transparent"
                                            >
                                                {cta}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                ),
            })}
        </>
    );
};
